/**
 * Dock halves that used to share the Live tab: banner, Sentinel, activity.
 */
import React from 'react';
import { Typography } from '@mui/material';
import { Mission, MissionRouteStatus, SentinelAction } from '../../services/missionApi';
import MissionAlertWaterfall from './MissionAlertWaterfall';
import MissionDockBanner from './MissionDockBanner';
import MissionSentinel from './MissionSentinel';
import { RouteOptionPreview } from './map/MissionOptionPreviewLayer';
import { useMissionRunWatch } from './useMissionRunWatch';

export function useMissionDockLive(args: {
  mission: Mission | null;
  deviceLabel?: (deviceId: string) => string;
  units?: 'km' | 'mi';
  onError?: (message: string | null) => void;
  onMissionChange: () => void;
  onStatus?: (status: MissionRouteStatus) => void;
  onFocus: (longitude: number, latitude: number) => void;
  onPreviewOption?: (preview: RouteOptionPreview | null) => void;
}) {
  const deviceLabel = args.deviceLabel || ((id: string) => id);
  const units = args.units || 'mi';
  const watch = useMissionRunWatch(args.mission, {
    onError: args.onError,
    onStatus: args.onStatus,
    onMissionChange: args.onMissionChange,
  });

  if (!args.mission) {
    return {
      banner: null as React.ReactNode,
      sentinel: null as React.ReactNode,
      activity: null as React.ReactNode,
    };
  }

  const mission = args.mission;
  return {
    banner: (
      <MissionDockBanner
        missionId={mission.id}
        events={watch.events}
        deviceLabel={deviceLabel}
        units={units}
        progress={watch.status?.progress}
        timeline={watch.status?.timeline}
        onAcked={watch.apply}
        onFocus={args.onFocus}
      />
    ),
    sentinel: watch.hasRoute ? (
      <MissionSentinel
        key={mission.id}
        mission={mission}
        onError={args.onError || (() => undefined)}
        onMissionChange={args.onMissionChange}
        refreshKey={watch.compareKey}
        offRoute={watch.offRoute}
        status={watch.status}
        onPreviewOption={args.onPreviewOption}
        fill
      />
    ) : (
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
        Plan a route for this mission to ask Sentinel while it runs.
      </Typography>
    ),
    activity: (
      <MissionAlertWaterfall
        key={mission.id}
        mission={mission}
        events={watch.events}
        deviceLabel={deviceLabel}
        units={units}
        checking={watch.checking}
        emptyHint={watch.running ? 'Nothing to report.' : 'No updates yet.'}
        onFocus={args.onFocus}
        onAcked={watch.apply}
        onRouteAround={(action: SentinelAction) => { watch.routeAround(action); }}
        routing={watch.routing}
        timeline={watch.status?.timeline}
      />
    ),
  };
}
